import { Box, Collapse, Icon, List, Tag, Text, useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { NavLink, useLocation } from 'react-router-dom';

import Tooltip from 'components/Tooltip';

import NestedSidebarItem from '../NestedSidebarItem';

const SidebarItem = ({ label, icon, path, children, isNavOpen, onNavClose, isNew }: any) => {
  const { t } = useTranslation(['common']);

  const location = useLocation();

  const { isOpen, onToggle, onOpen } = useDisclosure();
  const { isOpen: isTooltipOpen, onOpen: onTooltipOpen, onClose: onTooltipClose } = useDisclosure();

  const hasChildren = useMemo(() => {
    return Array.isArray(children) && children.length ? true : false;
  }, [children]);

  useEffect(() => {
    if (hasChildren && location.pathname.includes(path)) {
      onOpen();
    }
  }, [location.pathname, hasChildren, path, onOpen]);

  return (
    <>
      <Tooltip
        hasArrow
        arrowSize={10}
        placement="right"
        bg="secondary.500"
        label={!hasChildren ? t(label) : ''}
        isDisabled={isNavOpen}
        py="2"
        px="3"
        borderRadius="lg"
        // controlled isOpen tooltips only for menu having nested nav items
        {...(hasChildren ? { isOpen: !isNavOpen && isTooltipOpen } : {})}
      >
        <Box
          as={NavLink}
          to={path}
          display="flex"
          position="relative"
          py="3"
          px="3"
          alignItems="center"
          justifyContent={isNavOpen ? 'initial' : 'center'}
          _hover={{ bg: 'rgba(255,255,255, 0.1)', cursor: 'pointer', textDecoration: 'none' }}
          sx={{ '&.active': { bg: 'rgba(255,255,255, 0.1)', cursor: 'pointer' } }}
          borderRadius="2px"
          // controlled onMouseEnter and onMouseLeave event only for menu having nested nav items
          {...(hasChildren ? { onMouseEnter: onTooltipOpen, onMouseLeave: onTooltipClose } : {})}
          onClick={() => {
            if (onNavClose) {
              onNavClose();
            }
          }}
        >
          <Icon as={icon} height="20px" width="20px" sx={{ '& > path': { fill: '#ffffff' } }} />
          <Box
            ml="3"
            display={isNavOpen ? 'flex' : 'none'}
            transition="all 4s"
            overflow="hidden"
            justifyContent="space-between"
            w="full"
            alignItems="center"
          >
            <Text fontSize="14px" lineHeight="21px" fontWeight="300">
              {t(label)}
            </Text>
          </Box>
          {isNew ? (
            <Tag
              background="secondary.700"
              color="white"
              w="fit-content"
              display="flex"
              alignItems="center"
              position="absolute"
              right="1"
              top="1"
              size="xs"
              fontSize="9px"
              p="1"
              py="0.5"
              borderRadius="lg"
              textTransform="uppercase"
              fontWeight="normal"
            >
              {t('common:new')}
            </Tag>
          ) : null}

          {/* Small nav hoverable nested menu */}
          {hasChildren && isTooltipOpen && !isNavOpen ? (
            <List
              onMouseEnter={onTooltipOpen}
              onMouseLeave={onTooltipClose}
              position="absolute"
              top="0"
              left="100%"
              background="secondary.500"
              borderRadius="lg"
            >
              {children.map((child: any) => {
                return <NestedSidebarItem key={child.path} {...child} isNavOpen={isNavOpen} onNavClose={onNavClose} />;
              })}
            </List>
          ) : null}

          {/* Large nav collapsible menu trigger */}
          {hasChildren && isNavOpen ? (
            <Icon
              width="20px"
              height="20px"
              padding="2px"
              as={isOpen ? FaAngleUp : FaAngleDown}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onToggle();
              }}
            />
          ) : null}
        </Box>
      </Tooltip>

      {/* Large nav collapsible nested menu */}
      {hasChildren && isNavOpen ? (
        <Collapse in={isOpen} animateOpacity>
          <List>
            {children.map((child: any) => {
              return <NestedSidebarItem key={child.path} {...child} isNavOpen={isNavOpen} onNavClose={onNavClose} />;
            })}
          </List>
        </Collapse>
      ) : null}
    </>
  );
};

export default SidebarItem;
